import React, { useState, useCallback } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { useQueryClient } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { removePaymentMethod } from '@/data/giving';
import { paymentMethodReturnWarning } from '@/utils/paymentMethodStatus';

const isApplePayMethod = (method) => (method.methodType || '').toUpperCase() === 'APPLE_PAY';

const formatMethodLabel = (method) => {
    const mt = (method.methodType || '').toUpperCase();
    if (mt === 'ACH' || mt === 'BANK') {
        const bankName = method.achBankName || 'Bank';
        const last4 = method.achAccountNumberLast4 || '';
        return `${bankName} •••• ${last4 || '0000'}`;
    }
    const brand = method.cardBrand || 'Card';
    const last4 = method.cardLast4 || '';
    if (isApplePayMethod(method)) {
        return `Apple Pay — ${brand} •••• ${last4}`;
    }
    return `${brand} ending in ${last4 || '••••'}`;
};

const methodIconName = (method) => {
    const mt = (method.methodType || '').toUpperCase();
    if (mt === 'ACH' || mt === 'BANK') return 'business-outline';
    if (mt === 'APPLE_PAY') return 'logo-apple';
    return 'card-outline';
};

export default function ManagePaymentMethodsModal({ isOpen, onClose, methods = [], organizationID, personID }) {
    const { theme: { colors } } = useUnistyles();
    const queryClient = useQueryClient();
    const [removingID, setRemovingID] = useState(null);

    const handleRemove = useCallback(async (method) => {
        const pmID = method.paymentMethodID || method.id;
        setRemovingID(pmID);
        try {
            await removePaymentMethod(pmID);
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.paymentMethods(organizationID, personID) });
            Toast.show({ type: 'success', text2: 'Payment method removed' });
        } catch (err) {
            Toast.show({ type: 'error', text2: err?.message || 'Failed to remove payment method' });
        } finally {
            setRemovingID(null);
        }
    }, [queryClient, organizationID, personID]);

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            onCancel={onClose}
            title="Manage Payment Methods"
            subtitle="Remove saved payment methods"
        >
            <View style={styles.container} testID="manage-payment-methods">
                {methods.length === 0 ? (
                    <View style={styles.emptyContainer}>
                        <Unicon name="card-outline" size={32} colorVariant="textTertiary" />
                        <Text style={styles.emptyText}>No saved payment methods.</Text>
                    </View>
                ) : (
                    methods.map((m) => {
                        const pmID = m.paymentMethodID || m.id;
                        return (
                            <View key={pmID} style={styles.methodRow} testID={`manage-pm-${pmID}`}>
                                <Unicon
                                    name={methodIconName(m)}
                                    size={20}
                                    colorVariant="textSecondary"
                                />
                                <View style={styles.methodDetails}>
                                    <Text style={styles.methodLabel}>{formatMethodLabel(m)}</Text>
                                    {m.achReturnedAt && <View style={styles.returnWarning} testID={`manage-pm-return-${pmID}`}>
                                        <Unicon name="warning" size={16} colorVariant="error" />
                                        <Text style={styles.returnWarningText}>{paymentMethodReturnWarning}</Text>
                                    </View>}
                                </View>
                                <Pressable
                                    testID={`manage-pm-remove-${pmID}`}
                                    onPress={() => handleRemove(m)}
                                    style={styles.removeBtn}
                                    disabled={removingID === pmID}
                                >
                                    {removingID === pmID ? (
                                        <ActivityIndicator size="small" color={colors.error} />
                                    ) : (
                                        <Unicon name="trash-outline" size={18} colorVariant="error" />
                                    )}
                                </Pressable>
                            </View>
                        );
                    })
                )}
            </View>
        </SheetModal>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, roundness, typography }) => ({
    container: {
        gap: spacing.sm,
        padding: spacing.sm,
    },
    emptyContainer: {
        alignItems: 'center',
        gap: spacing.sm,
        paddingVertical: spacing.xl,
    },
    emptyText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    methodRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        padding: spacing.md,
        borderRadius: roundness.sm,
        backgroundColor: colors.surfaceVariant,
    },
    methodDetails: { flex: 1, gap: 4 },
    returnWarning: { flexDirection: 'row', gap: spacing.xs, alignItems: 'flex-start' },
    returnWarningText: { flex: 1, fontFamily: fonts.body, fontSize: typography.caption, color: colors.error },
    methodLabel: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.text,
    },
    removeBtn: {
        padding: spacing.xs,
    },
}));
